import type { StructureNode } from '@kronos/domain'
import type { ComponentType } from '../../data'
import { Badge } from '../primitives'
import { NodeThumb } from './NodeThumb'
import { TreeIcon } from './treeIcons'
import { GAP, KIND, plural, qty, remedy, verdict } from './words'

/**
 * What the selected node is, and the buttons for doing something about it.
 *
 * A panel docked to the right of the diagram on a desktop and a sheet rising
 * from the bottom on a phone — the same element, placed by CSS. It overlays
 * the canvas rather than pushing it aside, so opening it never moves the
 * diagram out from under the card that was just tapped.
 */
export function StructureInspector({
  node,
  parentName,
  rootName,
  quantity,
  type,
  stockKnown,
  canEdit,
  onClose,
  onOpenItem,
  onEdit,
  onReroot,
}: {
  node: StructureNode
  parentName: string | null
  rootName: string
  quantity: number
  type: ComponentType | undefined
  stockKnown: boolean
  canEdit: boolean
  onClose: () => void
  /** Null hides it — in the item editor's preview, where leaving would lose the form. */
  onOpenItem: (() => void) | null
  onEdit: () => void
  /** Present when this node can be drawn as a root of its own. */
  onReroot: (() => void) | null
}): JSX.Element {
  const v = verdict(node)
  const gap = node.leaf === null ? undefined : GAP[node.leaf]
  /* "1986 each" says nothing "1986" does not; "3.2 m" does. */
  const unit = node.tracking === 'quantity' && type?.unit && !/^(each|ea|pcs?|pieces?|units?)$/i.test(type.unit) ? ` ${type.unit}` : ''
  const covered = node.perRoot - node.need
  const root = node.depth === 0

  return (
    <aside className="st-inspector" aria-label={`${node.typeName} details`}>
      <header className="st-inspector__head">
        <NodeThumb imageId={type?.imageId ?? null} kind={node.kind} consumed={node.leaf === 'consumed'} root={root} size={64} />
        <div className="st-inspector__title">
          <h3>{node.typeName}</h3>
          <span className="st-inspector__badges">
            {node.revisionName ? <span className="st-card__rev">{node.revisionName}</span> : null}
            <Badge tone={KIND[node.kind].tone}>{KIND[node.kind].label}</Badge>
            {gap ? <Badge tone={gap.tone}>{gap.label}</Badge> : null}
          </span>
        </div>
        <button type="button" className="ghost st-inspector__close" aria-label="Close details" onClick={onClose}>
          <TreeIcon name="close" />
        </button>
      </header>

      {parentName !== null ? (
        <p className="st-inspector__line">
          <strong>{qty(node.perParent)}{unit}</strong> in each {parentName}
          {node.perRoot !== node.perParent ? (
            <>
              {' '}· <strong>{qty(node.perRoot)}{unit}</strong> for {quantity === 1 ? 'one' : quantity} {rootName}
            </>
          ) : null}
        </p>
      ) : null}
      {node.positions.length > 0 ? (
        <div className="st-inspector__positions" aria-label="Positions">
          {node.positions.map((p) => (
            <span key={p} className="st-pos-chip">
              {p}
            </span>
          ))}
        </div>
      ) : node.leaf === 'consumed' ? (
        <p className="st-inspector__line muted">Consumed by the build — counted, never fitted to a position.</p>
      ) : null}

      {stockKnown && !root ? (
        <div className="st-inspector__stats">
          <Stat label="On shelf" value={`${qty(node.onShelf)}${unit}`} />
          <Stat label="Needed" value={`${qty(node.need)}${unit}`} />
          <Stat
            label={node.short === 0 && node.toMake > 0 ? 'To build' : 'Short'}
            value={`${qty(node.toMake)}${node.short > 0 ? unit : ''}`}
            tone={v.tone}
          />
        </div>
      ) : null}
      {stockKnown && !root && covered > 0 && node.need < node.perRoot ? (
        <p className="st-inspector__line muted">
          {qty(covered)} of the {qty(node.perRoot)} are already inside assemblies on the shelf.
        </p>
      ) : null}
      {stockKnown && node.short > 0 ? (
        <p className="st-inspector__line st-inspector__alert">
          {qty(node.short)}
          {unit} to {remedy(node.kind)}
          {node.leaf === 'needs_revision' ? ' — or give it a parts list so it can be built' : ''}.
        </p>
      ) : null}

      {node.children.length > 0 ? (
        <div className="st-inspector__made">
          <span className="section-label">Made of</span>
          <ul>
            {node.children.map((c) => (
              <li key={c.key}>
                <span className="st-row__qty">{qty(c.perParent)} ×</span> {c.typeName}
                {stockKnown && c.short + c.shortBelow > 0 ? <span className="st-dot" aria-label="short" /> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {type && (type.supplier || type.partNumber || type.leadTimeDays !== null) ? (
        <dl className="st-inspector__facts">
          {type.supplier ? <Fact term="Supplier" value={type.supplier} /> : null}
          {type.partNumber ? <Fact term="Part number" value={type.partNumber} /> : null}
          {type.leadTimeDays !== null ? <Fact term="Lead time" value={plural(type.leadTimeDays, 'day')} /> : null}
        </dl>
      ) : null}

      <div className="st-inspector__actions">
        {onOpenItem ? (
          <button type="button" className="primary" onClick={onOpenItem}>
            <TreeIcon name="open" /> Open item
          </button>
        ) : null}
        {canEdit && type ? (
          <button type="button" onClick={onEdit}>
            <TreeIcon name="edit" /> Edit
          </button>
        ) : null}
        {onReroot ? (
          <button type="button" onClick={onReroot}>
            <TreeIcon name="focus" /> Draw from here
          </button>
        ) : null}
      </div>
    </aside>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }): JSX.Element {
  return (
    <div className={`st-stat${tone ? ` st-stat--${tone}` : ''}`}>
      <span className="st-stat__label">{label}</span>
      <span className="st-stat__value">{value}</span>
    </div>
  )
}

function Fact({ term, value }: { term: string; value: string }): JSX.Element {
  return (
    <>
      <dt>{term}</dt>
      <dd>{value}</dd>
    </>
  )
}
